import { useEffect, useState } from "react";
import { AdminOrderService } from "../../../services/api/AdminOrderService/admin.order.service";
import { OrderService } from "../../../services/api/order.service";
import medicineIcon from "../../../assets/images/medicineIcon.png";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

interface ClinicItem {
  id: string;
  name: string;
  profileImage?: string;
}

export const AvailableStockReport = () => {
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const [clinicList, setClinicList] = useState<ClinicItem[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState<string>("");

  // Fetch Clinic list for admin filter
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const res = await OrderService.getClinicList();
        if (res && res.data) {
          setClinicList(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch clinic list:", err);
      }
    };
    fetchClinics();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleClinicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClinicId(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await AdminOrderService.getAdminProductListing(
          currentPage,
          10,
          searchQuery,
          selectedClinicId
        );

        const transformedProducts = (response.data || []).map((item: any) => ({
          product_id: item.product_id || item.id,
          product_name: item.product?.name || "Unknown Product",
          manufacturer_name:
            item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
          product_image: item.product?.productImage || medicineIcon,
          landing_cost: parseFloat(item.landing_cost) || 0,
          mrp: parseFloat(item.mrp) || 0,
          quantity: item.no_of_items || 0,
          expiry_date: item.expiry_date,
          batch_number: item.batch_number,
          hsn_number: item.hsn_number,
          product_rate: parseFloat(item.product_rate) || 0,
          discount: parseFloat(item.discount) || 0,
          finalSellingPrice:
            parseFloat(item.productPrice?.final_selling_price) || null,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          free_items: item.free_items || 0,
          remainingQuantity:
            item.remainingQuantity > 0
              ? item.remainingQuantity
              : item.remaining_stocks,
          tabletPerStrip: item.product?.tablet_per_stripe || 1,
          clinic_name: item.clinic?.name || "-",
          clinic_mobile: item.clinic?.mobileNumber || "-",
        }));

        setAvailableProducts(transformedProducts);
        setTotalPages(response.pagination?.totalPages || 1);
      } catch (err) {
        console.error("Failed to fetch available stock:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load products"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentPage, searchQuery, selectedClinicId]);

  const formatExpiryDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      return date.toLocaleDateString("en-IN", {
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  const handleExportToExcel = async () => {
    setDownloading(true);
    try {
      const BATCH_PAGE_SIZE = 100;

      // Step 1: Fetch first page in batch of 100 data
      const firstPageResponse = await AdminOrderService.getAdminProductListing(
        1,
        BATCH_PAGE_SIZE,
        searchQuery,
        selectedClinicId
      );

      if (!firstPageResponse.data || firstPageResponse.data.length === 0) {
        toast.error("No data found to export");
        return;
      }

      const totalBatchPages = firstPageResponse.pagination.totalPages;
      let allData: any[] = [...firstPageResponse.data];

      // Step 2: Fetch remaining pages in bunches of 100 (5 concurrent requests max)
      if (totalBatchPages > 1) {
        toast.loading(`Downloading data... (1/${totalBatchPages} pages)`, {
          id: "export-progress",
        });

        const CONCURRENT_LIMIT = 5;
        for (
          let startPage = 2;
          startPage <= totalBatchPages;
          startPage += CONCURRENT_LIMIT
        ) {
          const endPage = Math.min(
            startPage + CONCURRENT_LIMIT - 1,
            totalBatchPages
          );
          const pagePromises: Promise<any>[] = [];

          for (let page = startPage; page <= endPage; page++) {
            pagePromises.push(
              AdminOrderService.getAdminProductListing(
                page,
                BATCH_PAGE_SIZE,
                searchQuery,
                selectedClinicId
              )
            );
          }

          const results = await Promise.all(pagePromises);
          results.forEach((res) => {
            if (res.data && res.data.length > 0) {
              allData = [...allData, ...res.data];
            }
          });

          toast.loading(
            `Downloading data... (${Math.min(
              endPage,
              totalBatchPages
            )}/${totalBatchPages} pages)`,
            { id: "export-progress" }
          );
        }

        toast.dismiss("export-progress");
      }

      // Step 3: Convert accumulated data into Excel-friendly format
      const formattedData = allData.map((item: any) => {
        let expiryFormatted = "N/A";
        if (item.expiry_date) {
          const date = new Date(item.expiry_date);
          expiryFormatted = date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });
        }

        const tabletPerStrip = item.product?.tablet_per_stripe || 1;
        const quantity =
          item.remainingQuantity > 0
            ? item.remainingQuantity
            : item.remaining_stocks || 0;

        const remainingQuantity =
          tabletPerStrip > 1
            ? `Strips: ${Math.floor(
                quantity / tabletPerStrip
              )} Tablets: ${quantity % tabletPerStrip}`
            : `${quantity} Pieces`;

        return {
          "Clinic Name": item.clinic?.name || "N/A",
          "Clinic Phone": item.clinic?.mobileNumber || "N/A",
          "Product Name": item.product?.name || "N/A",
          "Batch Number": item?.batch_number || "N/A",
          "Invoice Number": item?.invoice_number || "N/A",
          "Invoice Date": item?.invoice_date
            ? new Date(item.invoice_date).toLocaleDateString("en-US")
            : "N/A",
          Rate: Number(item?.product_rate || 0).toFixed(2) || "N/A",
          MRP: Number(item?.mrp || 0).toFixed(2) || "N/A",
          "Selling Price":
            parseFloat(item.productPrice?.final_selling_price) || "N/A",
          "Expiry Date": expiryFormatted,
          "Remaining Quantity": remainingQuantity,
        };
      });

      // Step 4: Generate and download Excel sheet
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Available Stock");

      XLSX.writeFile(workbook, `available_stock_report.xlsx`);
      toast.success(`Exported ${allData.length} records successfully!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.dismiss("export-progress");
      toast.error("Error while exporting available stock");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <h1 className="text-xl font-bold text-gray-800">Available Stock Report</h1>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {/* Clinic Filter */}
          <div className="w-full sm:w-64">
            <select
              value={selectedClinicId}
              onChange={handleClinicChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm bg-white"
            >
              <option value="">All Clinics</option>
              {clinicList.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="w-full sm:w-64">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            />
          </div>

          {/* Export Button */}
          <button
            onClick={handleExportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            disabled={downloading}
          >
            {downloading ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex justify-center items-center py-12 text-gray-500">
            Loading available stock...
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600 font-medium">
            {error}
          </div>
        ) : availableProducts && availableProducts.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] table-auto">
                <thead className="border-b border-gray-200 bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      S.No.
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      Clinic Details
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      Product Name
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      Remaining Quantity
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      Rate
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      MRP
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      Selling Price
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      Expiry Date
                    </th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                      Batch Number
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {availableProducts.map((product: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500 font-medium">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="font-medium text-gray-900">
                          {product?.clinic_name || "-"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {product?.clinic_mobile || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 flex items-center">
                        <img
                          src={product?.product_image}
                          alt={product?.product_name}
                          className="w-10 h-10 rounded-lg mr-3 object-cover border border-gray-200"
                          onError={(e) => {
                            e.currentTarget.src =
                              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMjAgMTJDMTguODk1NCAxMiAxOCAxMi44OTU0IDE4IDE0QzE4IDE1LjEwNDYgMTguODk1NCAxNiAyMCAxNkMyMS4xMDQ2IDE2IDIyIDE1LjEwNDYgMjIgMTRDMjIgMTIuODk1NCAyMS4xMDQ2IDEyIDIwIDEyWiIgZmlsbD0iI0Q0RDRENCIvPgo8cGF0aCBkPSJNMTIgMjBDMTIgMTYuNjg2MyAxNC42ODYzIDE0IDE4IDE0SDIyQzI1LjMxMzcgMTQgMjggMTYuNjg2MyAyOCAyMFYyNEMyOCAyNy4zMTM3IDI1LjMxMzcgMzAgMjIgMzBIMThDMTQuNjg2MyAzMCAxMiAyNy4zMTM3IDEyIDI0VjIwWiIgZmlsbD0iI0Q0RDRENCIvPgo8L3N2Zz4K";
                          }}
                        />
                        <span className="font-medium text-gray-900">
                          {product?.product_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {product?.tabletPerStrip > 1
                          ? `Strips: ${Math.floor(
                              product?.remainingQuantity /
                                product?.tabletPerStrip
                            )} Tablets: ${
                              product?.remainingQuantity %
                              product?.tabletPerStrip
                            }`
                          : `${product?.remainingQuantity} Pieces`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        ₹{product?.product_rate}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        ₹{product?.mrp}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {product.finalSellingPrice
                          ? `₹${product.finalSellingPrice}`
                          : "Not Updated"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatExpiryDate(product?.expiry_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {product?.batch_number || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center py-3 px-4 bg-white border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                {Array.from(
                  { length: Math.min(5, totalPages) },
                  (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium ${
                          currentPage === pageNum
                            ? "bg-green-600 text-white"
                            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                        } transition-colors`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 5l7 7-7 7M5 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-gray-500 font-medium">
            No stock products found.
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailableStockReport;
